import { api } from './api';

interface RouteEvent {
    eventName: string;
    path: string;
    timestamp: string;
    details?: Record<string, unknown>;
}

class RouteTracker {
    private events: RouteEvent[] = [];
    private timeoutId: NodeJS.Timeout | null = null;
    private batchIntervalMs: number = 30000;
    private lastPath: string | null = null;
    private isEnabled: boolean = false;

    start() {
        this.isEnabled = true;
        this.scheduleBatchSend();
    }

    stop() {
        this.isEnabled = false;
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        this.events = [];
        this.lastPath = null;
    }

    trackNavigation(path: string, params?: Record<string, string | undefined>) {
        if (!this.isEnabled) {
            return;
        }

        if (path === this.lastPath) {
            return;
        }

        if (path.startsWith('/admin') || path.startsWith('/login') || path.startsWith('/unauthorized') || path === '/') {
            return;
        }

        this.lastPath = path;

        const event: RouteEvent = {
            eventName: 'musa.route.navigation',
            path,
            timestamp: new Date().toISOString(),
            details: params ? { params } : undefined,
        };

        this.events.push(event);
    }

    private scheduleBatchSend() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }

        this.timeoutId = setTimeout(() => {
            this.sendBatch();
            if (this.isEnabled) {
                this.scheduleBatchSend();
            }
        }, this.batchIntervalMs);
    }

    private async sendBatch() {
        if (this.events.length === 0) {
            return;
        }

        const eventsToSend = [...this.events];
        this.events = [];

        try {
            await api.auditClientEvents.batchLogClientEvents({
                events: eventsToSend,
            });
        } catch {
            // Silently fail
        }
    }

    /**
     * Manually flush pending events (e.g., on logout)
     */
    async flush() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        await this.sendBatch();
    }
}

// Export singleton instance
export const routeTracker = new RouteTracker();
